<script setup lang="ts">
/**
 * Change Profile 팝업 — 프로필 메뉴(우측 ProfileDD·좌측 SidebarProfile)의 Change Profile 로 연다. FullLayout 에 전역 1개.
 *
 * 목록 = 원래 회사(맨 위) + L_MULTI_CSP 프로필(이름순). 지금 회사에 Current 칩(클릭 비활성).
 * 행 클릭 → /api/changeprofile/switch → 성공하면 남는 저장값을 지우고 /dashboard 로 전체 새로고침.
 * 원래 회사 줄을 누르면 복귀. 상단 CSP Sync 버튼으로 프로필 추가(추가되면 목록 다시 읽음).
 */
import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useChangeProfileStore } from '@/shared/stores/changeProfileStore';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import { changeProfileApi, type ChangeProfileItem } from '@/shared/api/changeProfileApi';
import CspClientSyncDialog from '@/features/Entity/components/CspClientSyncDialog.vue';

const changeProfileStore = useChangeProfileStore();
const { isOpen } = storeToRefs(changeProfileStore);
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = computed(() => createSyncT(languageStore.currentLanguage));

interface ProfileRow extends ChangeProfileItem { isOwner: boolean }

const loading = ref(false);
const switching = ref(false);
const switchingCode = ref('');
const errorMsg = ref('');
const search = ref('');
const current = ref('');
const rows = ref<ProfileRow[]>([]);
const showCspSync = ref(false);

const sameCd = (a?: string | null, b?: string | null) =>
  String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();

const filteredRows = computed(() => {
  const q = search.value.trim().toUpperCase();
  if (!q) return rows.value;
  return rows.value.filter((r) =>
    String(r.name || '').toUpperCase().includes(q) || String(r.regNo || '').toUpperCase().includes(q));
});

const hasCspProfiles = computed(() => rows.value.some((r) => !r.isOwner));

async function loadList() {
  loading.value = true;
  errorMsg.value = '';
  try {
    const data = await changeProfileApi.list();
    current.value = data.current || '';
    rows.value = [
      ...(data.owner ? [{ ...data.owner, isOwner: true }] : []),
      ...(data.items || []).map((i) => ({ ...i, isOwner: false })),
    ];
  } catch {
    rows.value = [];
    errorMsg.value = t.value('menu.changeProfileFailed') || "Couldn't switch profile. Try again.";
  } finally {
    loading.value = false;
  }
}

watch(isOpen, (open) => {
  if (open) {
    search.value = '';
    void loadList();
  }
});

function close() {
  if (switching.value) return;
  changeProfileStore.close();
}

/** 전환 후 이전 회사 기준으로 남아 있는 화면 저장값 정리 (다른 탭은 그대로) */
function clearCompanyScopedStorage() {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && (k.startsWith('register.') || k.startsWith('preset'))) sessionStorage.removeItem(k);
    }
    sessionStorage.removeItem('taskListState');
    sessionStorage.removeItem('arTSignRecipients');
  } catch { /* 저장소 접근 불가 — 무시 */ }
  try {
    localStorage.removeItem('onlyoffice-plugin-clientCd');
  } catch { /* 무시 */ }
}

async function onSelect(row: ProfileRow) {
  if (switching.value || sameCd(row.code, current.value)) return;
  switching.value = true;
  switchingCode.value = row.code;
  errorMsg.value = '';
  try {
    await changeProfileApi.switch(row.code);
    clearCompanyScopedStorage();
    // Pinia 초기화·/me 다시 읽기 — 전체 새로고침
    window.location.assign('/dashboard');
  } catch (e: unknown) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    errorMsg.value = status === 404
      ? (t.value('menu.changeProfileNotFound') || 'Profile not found.')
      : (t.value('menu.changeProfileFailed') || "Couldn't switch profile. Try again.");
    switching.value = false;
  }
}

function onCspSynced() {
  void loadList();
}
</script>

<template>
  <v-dialog :model-value="isOpen" max-width="560" scrollable @update:model-value="(v: boolean) => { if (!v) close(); }">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t('menu.changeProfile') || 'Change Profile' }}</span>
        <div class="d-flex align-center ga-2">
          <v-btn
            color="primary"
            variant="tonal"
            size="small"
            prepend-icon="mdi-account-multiple-plus"
            :disabled="switching"
            @click="showCspSync = true"
          >
            {{ t('entity.CSP Sync') || 'CSP Sync' }}
          </v-btn>
          <v-btn icon="mdi-close" variant="text" size="small" :disabled="switching" @click="close" />
        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="search"
          :placeholder="t('menu.changeProfileSearch') || 'Search name or UEN'"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          class="mb-3"
        />

        <v-alert v-if="errorMsg" type="error" density="compact" variant="tonal" class="mb-3">
          {{ errorMsg }}
        </v-alert>

        <div v-if="loading" class="d-flex justify-center py-6">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <template v-else>
          <v-list density="compact" class="py-0">
            <v-list-item
              v-for="row in filteredRows"
              :key="row.code"
              rounded="md"
              class="mb-1"
              :disabled="switching || sameCd(row.code, current)"
              @click="onSelect(row)"
            >
              <v-list-item-title class="font-weight-medium">{{ row.name || row.code }}</v-list-item-title>
              <v-list-item-subtitle>
                <span v-if="row.regNo">{{ row.regNo }}</span>
                <span v-if="row.isOwner"> · {{ t('menu.changeProfileOwner') || 'Your company' }}</span>
              </v-list-item-subtitle>
              <template #append>
                <v-chip v-if="sameCd(row.code, current)" size="small" color="primary" variant="tonal">
                  {{ t('menu.changeProfileCurrent') || 'Current' }}
                </v-chip>
                <v-progress-circular v-else-if="switching && sameCd(row.code, switchingCode)" indeterminate size="16" width="2" />
              </template>
            </v-list-item>
          </v-list>

          <p v-if="!hasCspProfiles && !errorMsg" class="text-body-2 text-medium-emphasis mt-2 mb-0">
            {{ t('menu.changeProfileEmpty') || 'No CSP profiles yet. Use CSP Sync to add one.' }}
          </p>
        </template>
      </v-card-text>
    </v-card>

    <CspClientSyncDialog v-model="showCspSync" @synced="onCspSynced" />
  </v-dialog>
</template>
